"use client";

import { Suspense, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import {
  initAnalytics,
  capture,
  capturePageview,
  isAnalyticsEnabled,
} from "./client";
import { ANALYTICS_EVENTS } from "./events";
import type { AccountType } from "./events";
import { D2C_STOREFRONT_TENANT_ID } from "@/lib/commerce/constants";

/**
 * Best-effort account type from the URL alone.
 *
 * This must stay consistent with the `isD2C` rule in `app/design/page.tsx`:
 * a retailer locked via `?retailerId=` (other than the D2C storefront tenant)
 * is a retailer-branded GC flow; everything else on the consumer side falls back
 * to the Plantista storefront, which IS D2C. Previously this only ever returned
 * "pro"/"gc" while `design_started` returned "d2c", so any breakdown by
 * account_type disagreed with itself.
 *
 * The design page re-registers the resolved value as a super property once
 * retailer state settles, which supersedes this for every later event.
 */
function accountTypeFromUrl(
  pathname: string | null,
  retailerParam: string | null
): AccountType {
  if (pathname?.startsWith("/pro")) return "pro";
  if (retailerParam && retailerParam !== D2C_STOREFRONT_TENANT_ID) return "gc";
  return "d2c";
}

/**
 * Mounts the PostHog client (PLA2-366) and captures pageviews + a one-time
 * `session_started` per the spec. Wrapped in <Suspense> because it reads
 * useSearchParams. No-ops when NEXT_PUBLIC_POSTHOG_KEY is unset.
 */
function PageviewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    initAnalytics();
    if (!isAnalyticsEnabled()) return;

    // Once per browser session: session_started with the entry context.
    try {
      const KEY = "ph_session_started";
      if (!sessionStorage.getItem(KEY)) {
        sessionStorage.setItem(KEY, "1");
        const accountType = accountTypeFromUrl(
          pathname,
          searchParams?.get("retailerId") ?? null
        );
        capture(ANALYTICS_EVENTS.SESSION_STARTED, {
          account_type: accountType,
          source_url: pathname,
        });
      }
    } catch {
      /* sessionStorage unavailable — skip the session marker */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manual pageview on every route change.
  useEffect(() => {
    if (!isAnalyticsEnabled()) return;
    const qs = searchParams?.toString();
    capturePageview(qs ? `${pathname}?${qs}` : pathname || "");
  }, [pathname, searchParams]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Suspense fallback={null}>
        <PageviewTracker />
      </Suspense>
      {children}
    </>
  );
}
